import React, {useState, useEffect,useMemo} from 'react';
import {Row, Col, Button} from '../../bootstrap/index.jsx'
import EditableList from './EditableList.jsx';
import {ACTION_TYPES} from './phaseReducer.js';
import Select from '../../common/Select.jsx';
import {PhaseInfoDialogButton} from './PhaseInfoDialogButton.jsx';

import {
  monitoring_report_add_form_capture_config_link_path,
  monitoring_report_delete_form_capture_config_link_path,
  monitoring_report_previous_form_capture_config_links_path,
  programme_form_configure_data_capture_path,
  project_call_phase_capture_update_v2_path,
  project_call_phase_capture_delete_v2_path,
  project_call_phase_configure_data_capture_path,
} from '~/routes';

const EditableSelectCell = ({cell, row, updateCell, column: {id}, onChange, updateRow, options, programmeId, phaseId, isClearable = true, defaultValue = null}) => {
  const handleChange = val => {
    if (typeof onChange == 'function') {
      const value = onChange(val);

      if (typeof value == 'object' && (!cell.value || typeof cell.value != 'object')) {
        updateRow(row.index, {...row.original, ...value})
      } else {
        updateCell(row.index, id, value)
      }
    } else {
      updateCell(row.index, id, val)
    }
  };

  const configOptions = options.map((link) => (
    {
      label: link.name,
      options: link.capture_configs.map((cc) => ({label: cc.name, value: `${cc.id}-${link.id}-${link.context}`}))
    }
  ));

  const getLabel = () => {
    let captureConfig = null;
    let route = null;
    let [configId, linkId, linkContext] = cell.value?.split('-');
    let link = options.find(link => link.id == linkId);

    if (link) {
      captureConfig = link.capture_configs.find(cc => cc.id == configId);
      if (captureConfig) {
        route = linkContext == 'project_content' ?
          project_call_phase_configure_data_capture_path({phase_id: phaseId, phase_content_id: link.id}) :
          programme_form_configure_data_capture_path({programme_id: programmeId, form_id: link.form_id})
      }
    }
    return route ? <><a target='_blank' href={route}>{link.name}</a> {captureConfig.name}</>  : '-'
  };

  return row.original.editActive ?
    <div className='input-group input-group-sm full-width'>
      <Select
        options={configOptions}
        onChange={handleChange}
        value={cell.value}
        defaultValue={defaultValue}
        isClearable={isClearable}
        className='form-control nested-multiselect'
      />
    </div> : getLabel()
};

const EditableCheckBoxCell = ({cell,  row, column: { id }, updateCell, options}) => {
  const [value, setValue] = useState(cell.value);

  const onChange = e => {
    setValue(e.target.checked);
    updateCell(row.index, id, e.target.checked)
  };

  const isTargetingForm = useMemo(
    () => {
      const target_type = options.find(v => v.id == row.original.link_id)?.capture_configs?.find(v => v.id == row.original.config_id)?.target_type;
      return ['form_answer', 'project'].includes(target_type);
    }, [row.original]
  );

  useEffect(() => {
    setValue(isTargetingForm ? cell.value : false);
  }, [cell.value, isTargetingForm]);

  return row.original.editActive && isTargetingForm ?
    <div className='input-group input-group-sm full-width'>
      <input type='checkbox' value={value} checked={value} onChange={onChange} />
    </div>
    :
    I18n.t(`option_${value ? 'yes' : 'no'}`)
};

const ActionCell = (
  {
    row,
    updateCell,
    onSaveRow,
    onDeleteRow,
    updateRow,
    removeRow,
    discardRow
  }) => {
  const [saving, setSaving] = useState(false);

  const onCancelClick = (_e) => {
    updateCell(row.index, 'editActive', false);
    discardRow(row.index);
  };

  const onSaveClick = (_e) => {
    let row_original = row.original;

    if (row_original.config_id && row_original.link_id) {
      setSaving(true);

      onSaveRow({...row_original}, (form_capture_config_link_id) => {
        updateRow(row.index, {...row_original, editActive: false, id: form_capture_config_link_id});
        setSaving(false);
      })
    }
  };

  const onDeleteClick = (_e) => {
    if (confirm(I18n.t('data_capture_remove_confirm'))) {
      setSaving(true);
      onDeleteRow({...row.original}, () => {
        removeRow(row.index);
      })
    }
  };

  return <div className='pull-right'>
    { row.original.editActive ?
      <>
        <Button disabled={saving} size='sm' onClick={onSaveClick}><i className='fa fa-check'/></Button>
        <Button disabled={saving} size='sm' onClick={onCancelClick} className='ml-1' type='default'><i className='fa fa-times'/></Button>
      </>
      :
      <Button disabled={saving} size='sm' onClick={onDeleteClick} className='ml-1' type='danger'><i className='fa fa-trash'/></Button>
    }
  </div>
};

const DataCaptureListV2 =
  ({
     phase: {forms: forms, id: phaseId, type: phaseType, form_capture: form_capture, fund_id: fund_id},
     report = null,
     dispatch = null,
     initialConfigOptions = [],
     showTitle = true
  }) => {
  const [formCaptureConfigOptions, setConfigOptions] = useState(initialConfigOptions);
  const isFillPhase = (phaseType === 'fill');

  useEffect(() => {
    if (!report) return; // Called in ProjectCallPhaseCapture context. Options provided as props

    fetch(
      monitoring_report_previous_form_capture_config_links_path({id: report.id, phase_id: phaseId, _options: true})
    )
    .then(res => res.json())
    .then(data => setConfigOptions(data))
  }, [forms]);
  
  const columns = useMemo(
    () => [
      {
        id: 'form',
        Header: I18n.t('form'),
        accessor: ({config_id, link_id, context}) => `${config_id}-${link_id}-${context}`,
        Cell: (props) => <EditableSelectCell onChange={onUpdateForm} options={formCaptureConfigOptions} programmeId={fund_id} phaseId={phaseId} {...props} />
      },
      isFillPhase ? {
        id: 'allow_capture_in_drafts',
        Header: <span id='optional-header'>{I18n.t('allow_capture_in_drafts')}</span>,
        accessor: (originalRow) => originalRow.allow_capture_in_drafts ? originalRow.allow_capture_in_drafts : '',
        Cell: (props) => <EditableCheckBoxCell options={formCaptureConfigOptions} {...props} />
      } : null,
      {
        id: 'actions',
        Header: '',
        accessor: 'id',
        Cell: ActionCell
      }
    ].filter((c) => c),
    [formCaptureConfigOptions]
  );

  const onSaveRow = (row, callback) => {
    let route = report ?
      monitoring_report_add_form_capture_config_link_path({id: report.id, phase_id: phaseId, _options: true}) :
      project_call_phase_capture_update_v2_path({phase_id: phaseId, _options: true})

    jQuery.post(route, {form_capture_config_link: row}, function (data) {
      if (dispatch) {
        dispatch({
          type: ACTION_TYPES.ADD_PHASE_DATA_CAPTURE_V2,
          payload: {
            newPhase: {
              config_id: row.config_id,
              ...(row.context == 'project_content' ? {phase_content_id: row.link_id} : {form_link_id: row.link_id}),
              id: data.id
            }, phaseId
          },
        });
      }

      callback(data.id);
    })
  };

  const onDeleteRow = (row, callback) => {
    let route = report ?
      monitoring_report_delete_form_capture_config_link_path({id: report.id, form_capture_config_link_id: row.id, phase_id: phaseId, _options: true}) :
      project_call_phase_capture_delete_v2_path({form_capture_config_link_id: row.id, phase_id: phaseId, _options: true})

    jQuery.post(route, {}, function (_data) {
      if (dispatch) {
        dispatch({type: ACTION_TYPES.REMOVE_PHASE_DATA_CAPTURE_V2, payload: {rowId: row.id, phaseId}});
      }
      callback(row.id);
    })
  };

  const onUpdateForm = (value) => {
    const val = value.split('-');
    return {config_id: val[0], link_id: val[1], context: val[2]};
  };

  const defaultRow = useMemo(
    () => ({config_id: null, link_id: null, context: null}),
    []
  );

  return <Row>
      <Col>
        <h3>{showTitle && I18n.t('data_capture')}
          <span>
            <PhaseInfoDialogButton
              title={I18n.t('data_capture')}
              info={I18n.t('data_capture_info')}
            />
          </span>
        </h3>
        {showTitle && <span className='description'>{I18n.t('form_capture_2nd_generation_capture_description')}</span>}
      </Col>
      <Col>
        <EditableList
          data={form_capture}
          columns={columns}
          defaultRow={defaultRow}
          draggable={false}
          addRowLabel={I18n.t('add_data_capture')}
          cellMethods={{onSaveRow: onSaveRow, onDeleteRow: onDeleteRow}}
        />
      </Col>
    </Row>
};

export default DataCaptureListV2;
